import React from "react";

import { TFunction, formatNumber, formatNumericCurrency, useGetFarms, useInventoryImpact, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { IncentiveData, IncentiveName, IncentivesPotentialPayouts, SingleFieldOutcomes } from "models";

import { Flex, IconLinkButton, Tag, Tooltip } from "components";

export type EarnedIncentivesAndTotalPayout = {
  earnedIncentives: {
    [incentiveName: string]: IncentiveData;
  };
  earnedTotalPayout: number;
};

export type PotentialIncentivesAndTotalPayout = {
  practicePotentialIncentives: {
    [incentiveName: string]: IncentiveData;
  };

  practicePotentialTotalPayout: number;
};

export type CurrentIncentivePayouts = {
  [incentiveName in IncentiveName]?: IncentiveData["total"];
};

type IncentiveValueDisplayProps = {
  currentPayoutsValue: number;
  t: TFunction;
  potentialValue?: number;
  currency?: CurrencyCode;
  maxedOut?: boolean;
  isDividePerHectare: boolean;
  displayPerHectareOrTotalFarm: (value: number, isPerHectare: boolean) => number;
};

export const aggregatePracticeBasedIncentiveCurrentPayouts = (
  singleFieldOutcomes: SingleFieldOutcomes[],
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined
) => {
  const currentPayouts: CurrentIncentivePayouts = {};
  for (const field of singleFieldOutcomes) {
    if (incentivesPotentialPayouts?.practicePotentialIncentives) {
      for (const name of Object.keys(incentivesPotentialPayouts.practicePotentialIncentives) as IncentiveName[]) {
        const totalPayout = field.practiceIncentives?.earnedIncentives?.[name]?.total;

        const newPayoutValue = (currentPayouts[name] || 0) + (totalPayout || 0);
        const maxPossibleValue = incentivesPotentialPayouts.practicePotentialIncentives[name].total;
        currentPayouts[name] = newPayoutValue > maxPossibleValue ? maxPossibleValue : newPayoutValue;
      }
    }
  }

  return currentPayouts;
};

export const aggregateIncentiveCurrentPayouts = (
  singleFieldOutcomes: SingleFieldOutcomes[],
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined
) => {
  const currentPayouts: CurrentIncentivePayouts = {};
  for (const field of singleFieldOutcomes) {
    if (incentivesPotentialPayouts?.practicePotentialIncentives) {
      for (const name of Object.keys(incentivesPotentialPayouts.practicePotentialIncentives) as IncentiveName[]) {
        const totalPayout = field.practiceIncentives?.earnedIncentives?.[name]?.total;
        currentPayouts[name] = (currentPayouts[name] || 0) + (totalPayout || 0);
      }
    }
  }
  return currentPayouts;
};

const formatValue = (payoutValue: number) =>
  formatNumber({ number: payoutValue, decimalPlaces: 0 }).toString() === "0" ? "—" : formatNumber({ number: payoutValue, decimalPlaces: 0 }).toString();

const IncentiveValueDisplay = ({
  currentPayoutsValue,
  potentialValue,
  // maxedOut,
  currency,
  t,
  isDividePerHectare,
  displayPerHectareOrTotalFarm,
}: IncentiveValueDisplayProps) => {
  const isPotentialGreaterThanCurrent = potentialValue !== undefined && potentialValue > currentPayoutsValue;
  const displayedCurrentPayoutsValue = displayPerHectareOrTotalFarm(currentPayoutsValue, isDividePerHectare);
  const displayedPotentialValue = displayPerHectareOrTotalFarm(potentialValue || 0, isDividePerHectare);

  return (
    <Flex align="center" gap="0.5rem" justify="space-between" className="width">
      <Flex align="center" justify="flex-start" gap="0.5rem">
        <Tooltip placement="topRight" title={t("impactPanel.inventory.practiceEarned")}>
          <span data-testid="inventory-impact-panel-current-payouts-value" className="green padding-left-300">
            {formatValue(isPotentialGreaterThanCurrent ? displayedCurrentPayoutsValue : displayedPotentialValue)}
          </span>
        </Tooltip>
      </Flex>
      <Flex align="center" justify="flex-start" className="width padding-left-100" gap="0.5rem">
        <span className="light-green">{"/"}</span>
      </Flex>
      <Flex align="center" justify="flex-end">
        <Tooltip placement="top" title={t("impactPanel.inventory.practicePotential")}>
          <span data-testid="inventory-impact-panel-incentives-potential-payouts-value" className="light-green">
            {formatNumericCurrency(displayedPotentialValue, { currency, decimals: 0 })}
          </span>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export const PracticeBasedImpact = ({
  singleFieldOutcomes,
  reportingYear,
  isDividePerHectare,
  currency,
}: {
  singleFieldOutcomes: SingleFieldOutcomes[];
  reportingYear: number;
  isDividePerHectare: boolean;
  currency?: CurrencyCode;
}) => {
  const { t } = useTranslation("fieldTimeline");
  const { currentFarm } = useGetFarms();
  const {
    allPracticesMaxedOut,
    totalPracticeBasedPayout,
    isPracticePotentialFullyUtilized,
    incentivesCurrentPayouts,
    practicePotentialPayoutTotal,
    incentivesPotentialPayouts,
    displayPerHectareOrTotalFarm,
  } = useInventoryImpact(reportingYear, singleFieldOutcomes, currentFarm?.id ?? "", currentFarm?.carbonProgram ?? undefined);

  const displayedPracticePotentialPayoutTotal = displayPerHectareOrTotalFarm(practicePotentialPayoutTotal, isDividePerHectare);
  const displayedTotalBonusCurrentPayout = displayPerHectareOrTotalFarm(totalPracticeBasedPayout, isDividePerHectare);

  const getIncentiveBonusPerHectare = (key: string) => {
    const practice = currentFarm?.carbonProgram?.practices.find((practice) => practice.incentives.find((incentive) => incentive.name === key));

    return practice?.incentives.find((incentive) => incentive.name === key)?.amount ?? 0;
  };

  return (
    <div className="inventory-impact-panel practice-based-incentive">
      <Flex className="margin-top-50">
        <Tooltip placement="top" title={t("impactPanel.inventory.payoutPracticesTitle")}>
          <IconLinkButton
            dataTestId="inventory-impact-panel-practices-title-info-icon-button"
            theme="black-grey-bold"
            iconType="info"
            size="small"
            text={t("impactPanel.inventory.payoutsPractices.title")}
          />
        </Tooltip>
      </Flex>
      <Flex align="center" justify="space-between" className="margin-top-75">
        {!allPracticesMaxedOut ? (
          <Flex align="center" justify="flex-start" gap="0.5rem">
            <Tooltip placement="top" title={t("impactPanel.inventory.payoutPractices")}>
              <span data-testid="inventory-impact-panel-total-incentives-payouts" className="green">
                {formatNumber({ number: displayedTotalBonusCurrentPayout, decimalPlaces: 0 })}
              </span>
            </Tooltip>
          </Flex>
        ) : (
          <Tag
            colour="yellow"
            className="payout-tag"
            dataTestId="inventory-impact-panel-practices-max-bonus-tag"
            size="large"
            text={t("impactPanel.inventory.payoutsPractices.maxBonus")}
          />
        )}

        <Flex align="center" justify="flex-end" gap="0.5rem">
          <Tooltip placement="top" title={t("impactPanel.inventory.payoutPracticesPotential")}>
            <span
              data-testid="inventory-impact-panel-total-incentives-payouts-potential"
              className={isPracticePotentialFullyUtilized ? "green" : "light-green"}
            >
              {formatNumericCurrency(displayedPracticePotentialPayoutTotal, { currency, decimals: 0 })}
            </span>
          </Tooltip>
        </Flex>
      </Flex>
      {(Object.entries(incentivesCurrentPayouts) as [currentPayoutsKey: IncentiveName, currentPayoutsValue: number][]).map(
        ([currentPayoutsKey, currentPayoutsValue], index) => {
          const bonusPerHectare = getIncentiveBonusPerHectare(currentPayoutsKey);

          return (
            <Flex key={index} align="center" className="margin-top-50">
              <Flex align="center" justify="space-between" className="practice-container">
                <span className="practice">{t(`impactPanel.inventory.payoutsPractices.incentives.${currentPayoutsKey}`)}</span>
                <Tooltip
                  placement="top"
                  title={
                    <>
                      <b>{t("impactPanel.inventory.payoutsPractices.tooltip.bonus", { amount: bonusPerHectare })}</b>
                      <br></br>
                      <span className="white-text">{t(`impactPanel.inventory.payoutsPractices.tooltip.${currentPayoutsKey}`)}</span>
                    </>
                  }
                >
                  <IconLinkButton dataTestId="inventory-impact-panel-practices-info-icon-button" theme="black-grey-bold" iconType="info" size="small" />
                </Tooltip>
              </Flex>

              <IncentiveValueDisplay
                currentPayoutsValue={currentPayoutsValue}
                potentialValue={incentivesPotentialPayouts?.practicePotentialIncentives?.[currentPayoutsKey]?.total}
                maxedOut={allPracticesMaxedOut}
                currency={currency}
                t={t}
                isDividePerHectare={isDividePerHectare}
                displayPerHectareOrTotalFarm={displayPerHectareOrTotalFarm}
              />
            </Flex>
          );
        }
      )}
      <div className="divider" />
    </div>
  );
};
